import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Carousel, Tabs } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import SpecDrawer from '../SpecDrawer/SpecDrawer';
import type { SpecDrawerProps } from '../SpecDrawer/SpecDrawer';
import { PhotoActionBar } from './PhotoActionBar';
import { ArrowCircleLeftIcon, ArrowCircleRightIcon, XIcon } from '../icons';
import { PhotoOverlayInfo, PhotoThumbnailInfo, PhotoMetadata } from './PhotoOverlayInfo';
import { handleGalleryImageLoad } from './galleryImageLoad';
import './PhotoGallery.css';

type DrawerSize = 'minimized' | 'default' | 'large';

export interface PhotoGalleryDrawerProps {
  /**
   * Whether the drawer is open
   */
  open: boolean;
  /**
   * Callback when the drawer is closed
   */
  onClose: () => void;
  /**
   * Array of photo URLs to display
   */
  photos: string[];
  /**
   * Title for the drawer (default: "Show Photos")
   */
  title?: string;
  /**
   * Initial drawer size (default: "large")
   */
  initialSize?: DrawerSize;
  /**
   * Whether to show tabs for Upcoming/Past filtering
   */
  showTabs?: boolean;
  /**
   * Photos for "Upcoming" tab (only used if showTabs is true)
   */
  upcomingPhotos?: string[];
  /**
   * Photos for "Past" tab (only used if showTabs is true)
   */
  pastPhotos?: string[];
  /**
   * Callback when the add-photo bar is clicked
   */
  onAddPhoto?: () => void;
  /**
   * Additional props to pass to SpecDrawer
   */
  drawerProps?: Partial<SpecDrawerProps>;
  /**
   * Fullscreen mode - drawer opens fully expanded, no dragging, X button to close
   * When true: always large size, no swipe gestures, X icon instead of chevrons
   */
  fullscreen?: boolean;
  /**
   * Per-photo metadata for the lightbox overlay (should match photos array length)
   */
  photoMetadata?: PhotoMetadata[];
  /**
   * Show the user-info lockup in the lightbox overlay
   */
  showOverlayUserInfo?: boolean;
  /**
   * Show the event-info lockup in the lightbox overlay
   */
  showOverlayEventInfo?: boolean;
}

/**
 * PhotoGalleryDrawer - A full-screen drawer showing all photos in a grid
 * 
 * Opens as a large drawer with all photos displayed in a responsive grid.
 * Clicking any photo opens the full-screen lightbox gallery with swipe navigation.
 * Supports optional Upcoming/Past tabs for filtering.
 */
export const PhotoGalleryDrawer: React.FC<PhotoGalleryDrawerProps> = ({
  open,
  onClose,
  photos,
  title = 'Show Photos',
  initialSize = 'large',
  showTabs = false,
  upcomingPhotos = [],
  pastPhotos = [],
  onAddPhoto,
  drawerProps,
  fullscreen = false,
  photoMetadata,
  showOverlayUserInfo = false,
  showOverlayEventInfo = false,
}) => {
  // In fullscreen mode, always use 'large' size
  const [drawerSize, setDrawerSize] = useState<DrawerSize>(fullscreen ? 'large' : initialSize);
  const [activeTab, setActiveTab] = useState('past');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPhotoList, setCurrentPhotoList] = useState<string[]>([]);
  const carouselRef = useRef<CarouselRef>(null);
  const isAnimatingRef = useRef(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine which photos to show based on tabs
  const displayPhotos = showTabs 
    ? (activeTab === 'upcoming' ? upcomingPhotos : pastPhotos)
    : photos;

  const openGallery = (index: number, photoList: string[]) => {
    setCurrentIndex(index);
    setCurrentPhotoList(photoList);
    setIsModalOpen(true);
  };

  const closeGallery = () => {
    setIsModalOpen(false);
  };

  const goToPrev = () => {
    if (isAnimatingRef.current) return;
    if (currentIndex <= 0) return;
    isAnimatingRef.current = true;
    carouselRef.current?.goTo(currentIndex - 1);
  };

  const goToNext = () => {
    if (isAnimatingRef.current) return;
    if (currentIndex >= currentPhotoList.length - 1) return;
    isAnimatingRef.current = true;
    carouselRef.current?.goTo(currentIndex + 1);
  };

  const handleBeforeChange = (current: number, next: number) => {
    // Only set animating if there's an actual slide change
    if (current !== next) {
      isAnimatingRef.current = true;
    }
  };

  const handleAfterChange = (current: number) => {
    // Clear any pending timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    // Use a small delay to ensure animation is truly complete before allowing next navigation
    animationTimeoutRef.current = setTimeout(() => {
      isAnimatingRef.current = false;
    }, 100);
    setCurrentIndex(current);
  };

  // Keyboard navigation and body scroll lock
  useEffect(() => {
    if (!isModalOpen) return;

    // Lock body scroll
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (currentIndex > 0) {
          goToPrev();
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (currentIndex < currentPhotoList.length - 1) {
          goToNext();
        }
      } else if (e.key === 'Escape') {
        closeGallery();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [isModalOpen, currentIndex, currentPhotoList.length]);

  const renderPhotoGrid = (photoList: string[]) => {
    if (photoList.length === 0) {
      return (
        <div className="photo-gallery-empty">
          No pics yet
        </div>
      );
    }

    return (
      <div className="photo-gallery-grid">
        {photoList.map((src, index) => (
          <div key={index} className="photo-gallery-grid-item">
            <img
              src={src}
              style={{ 
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => openGallery(index, photoList)}
              alt={`Photo ${index + 1}`}
            />
            {photoMetadata && <PhotoThumbnailInfo metadata={photoMetadata[index]} />}
          </div>
        ))}
      </div>
    );
  };

  const tabItems = [
    {
      key: 'past',
      label: 'Past',
      children: renderPhotoGrid(pastPhotos),
    },
    {
      key: 'upcoming',
      label: 'Upcoming',
      children: renderPhotoGrid(upcomingPhotos),
    },
  ];

  // Custom title with X button (always shown)
  const customTitle = (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
      {/* Drag handle placeholder to maintain spacing */}
      <div 
        style={{
          width: '40px',
          height: '4px',
          background: 'rgba(255, 255, 255, 0.3)',
          borderRadius: '2px',
          margin: '0 auto 24px',
          flexShrink: 0,
        }}
      />
      {title}
      {/* X close button - absolutely positioned */}
      <div
        style={{
          position: 'absolute',
          right: 0,
          top: '8px',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close drawer"
          className="drawer-close-button"
        >
          <XIcon size="m" tone="white" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <SpecDrawer
        title={customTitle}
        open={open}
        onClose={onClose}
        size={fullscreen ? 'large' : drawerSize}
        onSizeChange={fullscreen ? undefined : setDrawerSize}
        swipeable={false}
        closable={false}
        keyboard={!isModalOpen}
        footer={onAddPhoto ? (
          <div className="photo-gallery-drawer-footer">
            <PhotoActionBar onClick={onAddPhoto} />
          </div>
        ) : undefined}
        {...drawerProps}
      >
        {(fullscreen || drawerSize !== 'minimized') && (
          <div className="photo-gallery-drawer-content">
            {showTabs ? (
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                centered
              />
            ) : (
              renderPhotoGrid(displayPhotos)
            )}
          </div>
        )}
      </SpecDrawer>

      {isModalOpen && createPortal(
        <div className="photo-gallery-overlay">
          {/* Backdrop - click to close */}
          <div 
            className="photo-gallery-backdrop" 
            onClick={closeGallery}
            aria-hidden="true"
          />

          {/* Close button */}
          <button
            className="photo-gallery-close"
            onClick={closeGallery}
            aria-label="Close gallery"
          >
            <XIcon size="m" tone="white" />
          </button>

          {/* Navigation arrows */}
          <button
            className="photo-gallery-arrow photo-gallery-arrow-prev"
            onClick={goToPrev}
            disabled={currentIndex === 0}
            style={{ opacity: currentIndex === 0 ? 0.3 : 1 }}
            aria-label="Previous photo"
          >
            <ArrowCircleLeftIcon size="xl" tone="white" />
          </button>

          <button
            className="photo-gallery-arrow photo-gallery-arrow-next"
            onClick={goToNext}
            disabled={currentIndex === currentPhotoList.length - 1}
            style={{ opacity: currentIndex === currentPhotoList.length - 1 ? 0.3 : 1 }}
            aria-label="Next photo"
          >
            <ArrowCircleRightIcon size="xl" tone="white" />
          </button>

          {/* Carousel */}
          <div className="photo-gallery-carousel-wrapper">
            <Carousel
              ref={carouselRef}
              initialSlide={currentIndex}
              dots={false}
              swipeToSlide={false}
              draggable={false}
              beforeChange={handleBeforeChange}
              afterChange={handleAfterChange}
              speed={300}
              waitForAnimate
            >
              {currentPhotoList.map((src, idx) => (
                <div key={idx} className="photo-gallery-slide" onClick={closeGallery}>
                  <div className="photo-gallery-image-wrapper">
                    <img
                      src={src}
                      alt={`Photo ${idx + 1}`}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      onLoad={handleGalleryImageLoad}
                      style={{
                        userSelect: 'none',
                        WebkitUserDrag: 'none',
                      } as React.CSSProperties}
                    />
                    {photoMetadata && (showOverlayUserInfo || showOverlayEventInfo) && (
                      <PhotoOverlayInfo
                        showUserInfo={showOverlayUserInfo}
                        showEventInfo={showOverlayEventInfo}
                        metadata={photoMetadata[idx]}
                      />
                    )}
                  </div>
                </div>
              ))}
            </Carousel>
          </div>

          {/* Dot pagination */}
          <div className="photo-gallery-dots">
            {currentPhotoList.map((_, idx) => (
              <span
                key={idx}
                className={`photo-gallery-dot ${idx === currentIndex ? 'active' : ''}`}
              />
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default PhotoGalleryDrawer;
